import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter } from '@angular/material/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PolishDateAdapter } from '@/common/polish-date-adapter/polish-date-adapter';
import { DateService } from '@/common/date.service';
import { Data2Service } from '@/common/data2.service';
import { DataBagService } from '@/common/data-bag.service';
import { InfoboxService } from '@/common/infobox/infobox.service';

interface SettlementBonusNameOption {
	id: string;
	name: string;
	negative: string;
}

interface CurrencyOption {
	id: number;
	label: string;
}

export interface AddSettlementBonusDialogData {
	driverId: string;
	month: string;
}

@Component({
	selector: 'app-add-settlement-bonus-dialog',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		ReactiveFormsModule,
		MatDialogModule,
		MatFormFieldModule,
		MatInputModule,
		MatSelectModule,
		MatButtonModule,
		MatProgressSpinnerModule,
		MatDatepickerModule,
	],
	providers: [{ provide: DateAdapter, useClass: PolishDateAdapter }],
	templateUrl: './add-settlement-bonus-dialog.component.html',
	styles: [
		`
			.bonus-form {
				display: flex;
				flex-direction: column;
				gap: 14px;
				width: 100%;
				padding-top: 8px;
			}

			.bonus-form > mat-form-field {
				width: 100%;
			}

			.form-row {
				display: flex;
				flex-wrap: wrap;
				gap: 12px;
			}

			.form-row .field-value {
				flex: 1 1 160px;
			}

			.form-row .field-currency {
				flex: 1 1 110px;
			}

			.form-row mat-form-field {
				min-width: 0;
			}

			.form-hint {
				margin: 0 0 8px 4px;
				font-size: 12px;
				color: rgba(0, 0, 0, 0.6);
			}

			.dialog-loading {
				display: flex;
				justify-content: center;
				padding: 32px;
			}
		`,
	],
})
export class AddSettlementBonusDialogComponent implements OnInit {
	readonly types = signal<SettlementBonusNameOption[]>([]);
	readonly currencies = signal<CurrencyOption[]>([{ id: 1, label: 'PLN' }]);
	readonly loadingRefData = signal(true);
	readonly saving = signal(false);

	readonly selectedTypeHint = computed(() => {
		const type = this.types().find((item) => item.id === this.selectedTypeId());
		if (!type) {
			return '';
		}
		return type.negative === '1' ? 'Trafi do: Potrącenia w miesiącu' : 'Trafi do: Dodatki w miesiącu';
	});

	form!: FormGroup;

	private readonly dialogRef = inject(MatDialogRef<AddSettlementBonusDialogComponent>);
	private readonly data = inject<AddSettlementBonusDialogData>(MAT_DIALOG_DATA);
	private readonly fb = inject(FormBuilder);
	private readonly api = inject(Data2Service);
	private readonly bag = inject(DataBagService);
	private readonly infobox = inject(InfoboxService);
	private readonly dateService = inject(DateService);
	private readonly destroyRef = inject(DestroyRef);

	private readonly selectedTypeId = signal<string | null>(null);

	ngOnInit(): void {
		const [year, month] = (this.data.month ?? '').split('-').map(Number);
		const defaultDate = this.data.month ? new Date(year, month - 1, 1, 12) : new Date();

		this.form = this.fb.group({
			settlementBonusNameId: this.fb.control<string | null>(null, Validators.required),
			value: this.fb.control<number | null>(null, [Validators.required, Validators.min(0.01)]),
			date: this.fb.control<Date | null>(defaultDate, Validators.required),
			valueCurrencyId: this.fb.control<number>(1, Validators.required),
			comment: this.fb.control<string>(''),
		});

		this.loadReferenceData();
		this.form
			.get('settlementBonusNameId')
			?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((id: string | null) => this.selectedTypeId.set(id));
	}

	onSave(): void {
		if (this.form.invalid || this.saving()) {
			this.form.markAllAsTouched();
			return;
		}

		const raw = this.form.getRawValue();
		const value = Math.abs(Number(raw.value));
		const comment = raw.comment?.trim() ?? '';
		const currencyId = raw.valueCurrencyId;
		const date = this.dateService.datetimeWithTimezone(raw.date).date;

		const payload = {
			ownerId: this.data.driverId,
			date,
			settlementBonusNameId: raw.settlementBonusNameId,
			value,
			valueCurrencyId: currencyId,
			settlementCurrencyId: currencyId,
			comment,
			eventId: null,
			terms: [
				{
					value,
					month: date,
					isSettled: false,
					calculateToCash: 1,
					comment,
				},
			],
		};

		this.saving.set(true);
		this.api.post('/settlements/SettlementBonus/', payload, (result, error) => {
			if (error || !result) {
				this.saving.set(false);
				this.infobox.error(error?.error?.error ?? 'Nie udało się zapisać dodatku/potrącenia.');
				return;
			}
			this.infobox.success('Dodano dodatek/potrącenie.');
			this.dialogRef.close(true);
		});
	}

	onCancel(): void {
		this.dialogRef.close(null);
	}

	private loadReferenceData(): void {
		this.api.get<{ data: SettlementBonusNameOption[] } | false>('/settlements/settlementBonusName/', (result) => {
			if (result) {
				this.types.set(result.data ?? []);
			}
			this.loadingRefData.set(false);
		});
		this.bag.get(
			'/currency/?limit=50',
			(result: { results: { id: number; name: string; code?: string }[] } | false) => {
				if (result && result.results?.length) {
					this.currencies.set(result.results.map((item) => ({ id: item.id, label: item.code ?? item.name })));
				}
			},
		);
	}
}
